import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, operationsClient } from '@/lib/api/client';
import { qboReconciliationEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type { ReconciliationReport } from '@/types/qbo-reconciliation';
import { toast } from 'sonner';

interface ReportEnvelope {
  report: ReconciliationReport | null;
}

/**
 * Spec §9.6 — Latest reconciliation report for the active tenant.
 *
 * Returns `report: null` when no sweep has produced a snapshot yet
 * (fresh tenants or first 24h after enabling sync).
 */
export function useReconciliationReport() {
  return useApiQuery<ReportEnvelope>({
    queryKey: settingsKeys.qboReconciliationLatest(),
    queryFn: () =>
      apiGet<ReportEnvelope>(operationsClient, qboReconciliationEndpoints.latestReport()),
    refetchInterval: 60_000,
    staleTime: 30_000,
    showErrorToast: false,
  });
}

export function useReconciliationReportById(reportId: string | null) {
  return useApiQuery<ReportEnvelope>({
    queryKey: settingsKeys.qboReconciliationReport(reportId ?? 'none'),
    queryFn: () =>
      apiGet<ReportEnvelope>(operationsClient, qboReconciliationEndpoints.reportById(reportId!)),
    enabled: !!reportId,
    staleTime: 30_000,
    showErrorToast: false,
  });
}

/**
 * Manual "Run reconciliation now" trigger. Returns the freshly generated
 * report so the page can flip directly to the new snapshot.
 */
export function useRunReconciliation() {
  const queryClient = useQueryClient();
  return useApiMutation<ReportEnvelope, void>({
    mutationFn: () =>
      apiPost<ReportEnvelope, undefined>(operationsClient, qboReconciliationEndpoints.runManual()),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: settingsKeys.qboReconciliationLatest(),
      });
      toast.success('Reconciliation refresh queued');
    },
  });
}
